import { create } from 'zustand';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthStore {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  loginAttempts: Map<string, { count: number; lastAttempt: number }>;
  initialize: () => void;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: string | null; requiresConfirmation?: boolean; message?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  session: null,
  isLoading: true,
  error: null,
  initialized: false,
  loginAttempts: new Map(),

  initialize: () => {
    if (get().initialized) return;
    
    set({ initialized: true });

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        set({
          session,
          user: session?.user ?? null,
          isLoading: false,
        });
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      set({
        session,
        user: session?.user ?? null,
        isLoading: false,
      });
    });

    // Cleanup subscription on unmount (handled by React)
    return () => subscription.unsubscribe();
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });
    
    // Rate limiting: máximo 5 tentativas por email a cada 15 minutos
    const attempts = get().loginAttempts;
    const emailKey = email.toLowerCase();
    const now = Date.now();
    const attemptData = attempts.get(emailKey);
    
    if (attemptData) {
      const timeSinceLastAttempt = now - attemptData.lastAttempt;
      const fifteenMinutes = 15 * 60 * 1000;
      
      if (attemptData.count >= 5 && timeSinceLastAttempt < fifteenMinutes) {
        const remainingMinutes = Math.ceil((fifteenMinutes - timeSinceLastAttempt) / 60000);
        const errorMessage = `Muitas tentativas de login. Tente novamente em ${remainingMinutes} minuto(s).`;
        set({ error: errorMessage, isLoading: false });
        return { error: errorMessage };
      }
      
      // Reset contador se passou 15 minutos
      if (timeSinceLastAttempt >= fifteenMinutes) {
        attempts.set(emailKey, { count: 0, lastAttempt: now });
      }
    }
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Incrementar contador de tentativas
      const currentAttempts = attempts.get(emailKey) || { count: 0, lastAttempt: now };
      attempts.set(emailKey, {
        count: currentAttempts.count + 1,
        lastAttempt: now,
      });
      set({ loginAttempts: new Map(attempts) });
      
      let errorMessage = 'Erro ao fazer login. Tente novamente.';
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Email ou senha incorretos.';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Por favor, confirme seu email antes de fazer login.';
      }
      set({ error: errorMessage, isLoading: false });
      return { error: errorMessage };
    }

    // Login bem-sucedido: limpar tentativas
    attempts.delete(emailKey);
    set({ loginAttempts: new Map(attempts), isLoading: false });
    return { error: null };
  },

  signUp: async (email, password, name) => {
    set({ isLoading: true, error: null });

    // URL de redirecionamento após confirmação de email
    const redirectUrl = `${window.location.origin}/email-confirmation`;

    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name,
        },
      },
    });

    if (error) {
      let errorMessage = 'Erro ao criar conta. Tente novamente.';
      if (error.message.includes('User already registered')) {
        errorMessage = 'Este email já está cadastrado.';
      } else if (error.message.includes('Password should be')) {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres.';
      } else if (error.message.includes('Email rate limit')) {
        errorMessage = 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
      }
      set({ error: errorMessage, isLoading: false });
      return { error: errorMessage };
    }

    // Verificar se o email precisa ser confirmado
    // Se o Supabase estiver configurado para não exigir confirmação, data.user será null
    if (data.user && !data.session) {
      // Email precisa ser confirmado
      set({ isLoading: false });
      return { 
        error: null,
        requiresConfirmation: true,
        message: 'Por favor, verifique seu email para confirmar sua conta antes de fazer login.'
      };
    }

    set({ isLoading: false });
    return { error: null };
  },

  signOut: async () => {
    set({ isLoading: true });
    await supabase.auth.signOut();
    set({ user: null, session: null, isLoading: false });
  },

  resetPassword: async (email) => {
    set({ isLoading: true, error: null });

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      set({ error: 'Erro ao enviar email de recuperação.', isLoading: false });
      return { error: 'Erro ao enviar email de recuperação.' };
    }

    set({ isLoading: false });
    return { error: null };
  },

  clearError: () => set({ error: null }),
}));
